// src/views/layout/Fxbazzar.js — main shell: market watch, sidebar pages
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from "react-dom";
import '../../assets/scss/fxbazzar.scss';
import { FONT_ROBOTO } from '../../utils/fonts';
import TradingAccountsModal from '../../components/account/TradingAccountsModal/TradingAccountsModal';
import MarketWatch from "../trading/MarketWatch";
import OrderBook from '../pages/OrderBook';
import History from '../pages/History';
import PositionReport from '../pages/Positionreport';
import { useDispatch, useSelector } from "react-redux";
import { getIndexScript } from "Store/Reducer/marketwatch.slice";
import { getAvailableBalance, getRangeList, hideMessage } from "Store/Reducer/common.slice";
import { getMarginSummary } from "Store/Reducer/margin.slice";
import BannedBlockedScripts from '../pages/BannedBlockedScripts';
import ReportTrade from "views/pages/reports/ReportTrade";
import MarketWatchChart from "../../components/charts/MarketWatchChart";
import ChartToolRail from "../../components/charts/ChartToolRail";
import ManageDrawingsPanel from "../../components/charts/ManageDrawingsPanel";
import ChartHeaderControls from "../../components/charts/ChartHeaderControls";
import ReportLedger from "views/pages/reports/ReportLedger";
import ReportRejectionLog from "views/pages/reports/ReportRejectionLog";
import ReportUpdateLog from "views/pages/reports/ReportUpdateLog";
import TrialBalance from "views/pages/reports/TrialBalance";
import MaxQuantityDetails from '../pages/MaxQuantityDetails';
import WalletHubModal from '../../components/wallet/WalletHub/WalletHubModal';
import AccountMarginBar, { formatEquityPill } from "../../components/account/AccountMarginBar";
import {
  applyLivePnlToMarginSummary,
  sumLivePositionPnl,
} from "../../utils/marginSummaryLive";
import { setLastChartSelection } from "../../utils/lastChartSelection";
import MarketWatchQuickOrderModal from '../../components/trading/MarketWatchQuickOrderModal';
import DockTerminal from '../../components/trading/DockTerminal';
import ColorTemplatesPage, {
  TemplateCandleIcon,
  ColorTemplateCheckMark,
  applyColorTemplateId,
  colorTemplateLabel,
} from '../../components/charts/ColorTemplatesPage';
import ReportsMenuPage from '../../components/layout/ReportsMenuPage';
import AboutProgramModal, {
  AboutProgramContent,
} from '../../components/layout/AboutProgramModal';
import LanguagePage from '../../components/layout/LanguagePage';
import {
  APP_LANGUAGE_EVENT,
  applyAppLanguage,
  languageDisplayName,
  loadAppLanguage,
  saveAppLanguage,
  SELECTABLE_APP_LANGUAGES,
  t as tLang,
} from '../../utils/appLanguage';
import { pickIndexStripRows, indexRowLtp } from "utils/indexStripRows";
import { matchHotkey, shouldIgnoreGlobalHotkeys } from "utils/hotkeys";
import useViewport from "hooks/useViewport";
import useAppLanguage from "hooks/useAppLanguage";
import { useBrokerTimeZoneId } from "../../utils/dateFormats";
import { executeAction, openChartPropertiesMobile } from "../../components/charts/chartToolBridge";
import { Mt5MenuIcon } from "../../components/layout/Mt5MenuIcons";
import {
  CHART_PREF_EVENT,
  COLOR_TEMPLATES,
  getColorTemplate,
  loadChartPreferences,
} from "utils/chartPreferences";

/** Chart uses ResizeObserver; synthetic events cover flex-only resizes (watchlist drag, docks). */
function dispatchLayoutRefresh() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("resize"));
  window.dispatchEvent(new Event("fx-chart-layout"));
}

/** TEMP: hide NIFTY / BNIFTY / SENSEX strip under toolbar — set true to bring back. */
const SHOW_HEADER_INDICES = false;

/** In-app pages (not Chart). Persist via URL hash so refresh stays on the same page. */
const KNOWN_APP_PAGES = new Set([
  "history",
  "tradebook",
  "orderbook",
  "position",
  "banned",
  "maxqty",
  "trade-report",
  "ledger",
  "order-rejection",
  "order-editdelete",
  "trial-balance",
  "color-templates",
  "reports",
  "about-program",
  "language",
  "trading-accounts",
]);

const REPORT_APP_PAGES = new Set([
  "trade-report",
  "ledger",
  "order-rejection",
  "order-editdelete",
  "trial-balance",
]);

const SETTINGS_SUBPAGES = new Set([
  "color-templates",
  "reports",
  "about-program",
  "language",
  "trading-accounts",
]);

const HISTORY_TABS = new Set(["deals", "orders", "closed"]);

function normalizeAppPage(page) {
  if (!page || !KNOWN_APP_PAGES.has(page)) return null;
  // Mobile footer uses "tradebook"; both map to History.
  if (page === "tradebook") return "history";
  return page;
}

function normalizeHistoryTab(tab) {
  if (tab === "positions" || tab === "position") return "closed";
  if (HISTORY_TABS.has(tab)) return tab;
  return "deals";
}

/** Parse `#history/orders` → { page, historyTab }. */
function readHashRoute() {
  if (typeof window === "undefined") return { page: null, historyTab: "deals" };
  const raw = (window.location.hash || "").replace(/^#\/?/, "").split("?")[0].trim();
  if (!raw) return { page: null, historyTab: "deals" };
  const parts = raw.split("/").filter(Boolean);
  const page = normalizeAppPage(parts[0]);
  const historyTab =
    page === "history" && parts[1] ? normalizeHistoryTab(parts[1]) : "deals";
  return { page, historyTab };
}

function writeAppPageToHash(page, historyTab = "deals") {
  if (typeof window === "undefined") return;
  let nextHash = "";
  if (page === "history") {
    const tab = normalizeHistoryTab(historyTab);
    nextHash = tab && tab !== "deals" ? `#history/${tab}` : "#history";
  } else if (page) {
    nextHash = `#${page}`;
  }
  const curHash = window.location.hash || "";
  if (nextHash === curHash || (!nextHash && !curHash)) return;
  const base = `${window.location.pathname}${window.location.search}`;
  window.history.replaceState(null, "", nextHash ? `${base}${nextHash}` : base);
}

/** Flatten Map / array / object watchlist bags into row arrays. */
function collectWatchRows(bag) {
  if (!bag) return [];
  if (bag instanceof Map) return Array.from(bag.values());
  if (Array.isArray(bag)) return bag;
  if (typeof bag === "object") return Object.values(bag);
  return [];
}

/**
 * History deals/orders/positions are not Market Watch rows. Chart + New Order need a
 * watchlist row (script key, segmentId, live bid/ask). Match by script/symbol/displayName.
 */
function resolveWatchlistRow(symbolOrRow, marketWatchList, scriptIndexList) {
  const candidates = [];
  const push = (v) => {
    const s = String(v ?? "").trim().toUpperCase();
    if (s && !candidates.includes(s)) candidates.push(s);
  };
  if (symbolOrRow && typeof symbolOrRow === "object") {
    push(symbolOrRow.script);
    push(symbolOrRow.Script);
    push(symbolOrRow.symbol);
    push(symbolOrRow.Symbol);
    push(symbolOrRow.displayName);
    push(symbolOrRow.DisplayName);
    push(symbolOrRow.name);
  } else {
    push(symbolOrRow);
  }
  if (!candidates.length) return null;
  const rows = [
    ...collectWatchRows(marketWatchList),
    ...collectWatchRows(scriptIndexList),
  ];
  return (
    rows.find((r) => {
      if (!r || typeof r !== "object") return false;
      const keys = [r.script, r.displayName, r.symbol, r.name]
        .map((x) => String(x ?? "").trim().toUpperCase())
        .filter(Boolean);
      return keys.some((k) => candidates.includes(k));
    }) || null
  );
}

function chartKeyFromWatchRow(row, fallback = "") {
  return String(
    row?.script || row?.displayName || row?.symbol || row?.name || fallback || ""
  ).trim();
}

/** Min width where Symbol + Bid + Ask + LTP stay usable; drag cannot go narrower (use Hide instead). */
const WATCHLIST_PANEL_MIN_WIDTH = 358;
const WATCHLIST_PANEL_DEFAULT_WIDTH = 420;

/** Room the chart keeps when the position dock is dragged up. Volume overlays
 *  the price pane, so this only has to cover one pane plus the time axis. */
const CHART_MIN_HEIGHT = 220;
/** Usable table height once the dock is open. */
const POSITION_DOCK_MIN_HEIGHT = 120;
/** Thin grab strip when dock is collapsed — chart + marketwatch fill the rest. */
const POSITION_DOCK_COLLAPSED_HEIGHT = 10;
/** Release below this while dragging down → snap collapsed. */
const POSITION_DOCK_SNAP_COLLAPSE = 72;

const SHORTCUTS = [
  { sectionKey: "shortcutGlobal" },
  { key: "F1", descKey: "shortcutOpenHelp" },
  { key: "F2", descKey: "shortcutOpenWatchlist" },
  { key: "F3", descKey: "shortcutCollapsePosition" },
  { key: "Alt + T", descKey: "shortcutCollapsePosition" },
  { key: "Esc", descKey: "shortcutCloseDialog" },
  { sectionKey: "shortcutTrading" },
  { key: "F9", descKey: "shortcutOpenQuickOrder" },
  { key: "Ctrl + N", descKey: "shortcutOpenQuickOrder" },
  { key: "+ / =", descKey: "shortcutZoomIn" },
  { key: "- / _", descKey: "shortcutZoomOut" },
  { key: "← / →", descKey: "shortcutScrollChart" },
  { key: "Mouse wheel", descKey: "shortcutMouseWheel" },
  { key: "Ctrl + S", descKey: "shortcutSaveChart" },
];

/** Split "Alt + T" / "+ / =" into MT5-style key pills — does not change shortcut text. */
function shortcutKeyParts(key) {
  const raw = String(key || "").trim();
  if (!raw) return [];
  if (/mouse/i.test(raw)) return [raw];
  return raw
    .split(/\s+\/\s+|\s+\+\s+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const low = p.toLowerCase();
      if (low === "control") return "ctrl";
      if (low === "alt" || low === "ctrl" || low === "shift" || low === "cmd" || low === "meta") {
        return low;
      }
      return p;
    });
}

const REPORTS_NAV_CHILDREN = [
  { id:'trade-report',     iconId:'reportTrade',        labelKey:'tradeReport',        page:'trade-report'      },
  { id:'ledger',           iconId:'reportLedger',       labelKey:'ledgerReport',       page:'ledger'            },
  { id:'order-rejection',  iconId:'reportRejection',    labelKey:'orderRejectionLog',  page:'order-rejection'   },
  { id:'order-editdelete', iconId:'reportEditDelete',   labelKey:'orderEditDeleteLog', page:'order-editdelete'  },
  { id:'trial-balance',    iconId:'reportTrialBalance', labelKey:'trialBalance',       page:'trial-balance'     },
];

const SIDEBAR_NAV = [
  { id:'history',   iconId:'history',  label:'History',               page:'history'   },
  // Position Report removed from menu — Trade: web dock / mobile Trade page
  { id:'banned',    iconId:'banned',   label:'Banned/Blocked Scripts', page:'banned'    },
  { id:'maxqty',    iconId:'maxqty',   label:'Max Quantity Details',  page:'maxqty'    },
  {
    id:'reports', iconId:'reports', label:'Reports',
    children: REPORTS_NAV_CHILDREN,
  },
];

function getPageLabel(page, langId) {
  if (page === "color-templates") return tLang("colorTemplates", langId);
  if (page === "reports") return tLang("reports", langId);
  if (page === "about-program") return tLang("aboutProgram", langId);
  if (page === "language") return tLang("language", langId);
  if (page === "trading-accounts") return tLang("tradingAccounts", langId);
  if (page === "position") return tLang("trade", langId);
  if (page === "history") return tLang("history", langId);
  if (page === "banned") return tLang("banned", langId);
  if (page === "maxqty") return tLang("maxqty", langId);
  for (const item of SIDEBAR_NAV) {
    if (item.page === page) return item.labelKey ? tLang(item.labelKey, langId) : item.label;
    if (item.children) {
      for (const c of item.children) {
        if (c.page === page) return c.labelKey ? tLang(c.labelKey, langId) : c.label;
      }
    }
  }
  return '';
}

/** `only` narrows the strip to selected indices, so SENSEX can sit on the far
 *  right while the other two stay next to the chart controls. */
function HeaderIndexStrip({ scriptIndexList, only, className = "" }) {
  const { nifty, bankNifty, sensex } = useMemo(
    () => pickIndexStripRows(scriptIndexList),
    [scriptIndexList]
  );
  const items = [
    { label: "NIFTY 50", row: nifty },
    { label: "BNIFTY 50", row: bankNifty },
    { label: "SENSEX", row: sensex },
  ].filter(({ label }) => !only || only.includes(label));

  return (
    <div className={`top-index-strip${className ? ` ${className}` : ""}`}>
      {items.map(({ label, row }) => {
        const ltp = indexRowLtp(row);
        // Use pre-stored values from socket tick handler.
        // indexRowDisplayChange() recomputes from prevClose which is often 0/missing;
        // changeAmount / changePercent are set correctly by the tick handler.
        const delta = Number(row?.changeAmount ?? 0);
        const pctStr = row?.changePercent ?? "0.00";
        let chgClass = row?.changeAmountColor;
        if (!chgClass) {
          if (delta > 0) chgClass = "text-success";
          else if (delta < 0) chgClass = "text-danger";
          else chgClass = "";
        }
        const up = chgClass === "text-success";
        const down = chgClass === "text-danger";
        const color = up ? "#0f766e" : down ? "#b91c1c" : "#6b7280";
        const arrow = up ? "▲" : down ? "▼" : "•";
        const hasData = ltp > 0;
        return (
          <div key={label} className="top-index-item">
            <span className="idx-label">{label}</span>
            <span className="idx-values">
              <span className="idx-ltp">{hasData ? ltp.toFixed(2) : ""}</span>
              <span className="idx-chg" style={{ color }}>
                {hasData ? (
                  <>{arrow} {delta.toFixed(2)} ({pctStr}%)</>
                ) : null}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ThemeBridge({ darkTheme }) {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("fx-theme-dark", !!darkTheme);
    root.classList.toggle("fx-theme-light", !darkTheme);
    if (darkTheme) {
      // Match TradingView chart pane charcoal across the full app.
      root.style.setProperty('--bg-primary',    '#131722');
      root.style.setProperty('--bg-secondary',  '#1a1f2c');
      root.style.setProperty('--bg-panel',      '#131722');
      root.style.setProperty('--bg-header',     '#131722');
      root.style.setProperty('--bg-menu',       '#131722');
      root.style.setProperty('--border',        'rgba(255, 255, 255, 0.12)');
      root.style.setProperty('--text-primary',  '#ffffff');
      root.style.setProperty('--text-muted',    'rgba(255, 255, 255, 0.48)');
      root.style.setProperty('--green',         '#26a69a');
      root.style.setProperty('--red',           '#ef5350');
    } else {
      root.style.setProperty('--bg-primary',    '#ffffff');
      root.style.setProperty('--bg-secondary',  '#f8fafc');
      root.style.setProperty('--bg-panel',      '#ffffff');
      root.style.setProperty('--bg-header',     '#ffffff');
      root.style.setProperty('--bg-menu',       '#ffffff');
      root.style.setProperty('--border',        '#e5e7eb');
      root.style.setProperty('--text-primary',  '#1a1a1a');
      root.style.setProperty('--text-muted',    '#6b7280');
      root.style.setProperty('--green',         '#1a7a70');
      root.style.setProperty('--red',           '#cc2820');
    }
  }, [darkTheme]);
  return null;
}

function PageRouter({
  page,
  darkTheme,
  onColorTemplateApplied,
  historyTab,
  onOpenReport,
  tradingAccounts,
  languageId,
  onLanguageSelect,
}) {
  if (page === 'history')   return <History darkTheme={darkTheme} initialTab={historyTab || "deals"} />;
  if (page === 'tradebook') return <History darkTheme={darkTheme} initialTab={historyTab || "deals"} />;
  if (page === 'orderbook') return <OrderBook darkTheme={darkTheme} />;
  // Mobile Trade tab: MT5 position list (Account + Positions cards) — not Day/Net filters
  if (page === 'position')
    return (
      <div className="fx-mobile-trade-page-body">
        <PositionReport darkTheme={darkTheme} />
      </div>
    );
  if (page === 'banned')    return <BannedBlockedScripts darkTheme={darkTheme} />;
  if (page === 'maxqty')    return <MaxQuantityDetails darkTheme={darkTheme} />;
  if (page === 'reports')
    return (
      <ReportsMenuPage
        items={REPORTS_NAV_CHILDREN}
        onSelect={(reportPage) => onOpenReport?.(reportPage)}
      />
    );
  if (page === 'trade-report')     return <ReportTrade darkTheme={darkTheme} />;
  if (page === 'ledger')           return <ReportLedger darkTheme={darkTheme} />;
  if (page === 'order-rejection')  return <ReportRejectionLog darkTheme={darkTheme} />;
  if (page === 'order-editdelete') return <ReportUpdateLog darkTheme={darkTheme} />;
  if (page === 'trial-balance')    return <TrialBalance darkTheme={darkTheme} />;
  if (page === 'color-templates') return <ColorTemplatesPage onApplied={onColorTemplateApplied} />;
  if (page === 'language') return <LanguagePage languageId={languageId} onSelect={onLanguageSelect} />;
  if (page === 'about-program') return <AboutProgramContent asPage />;
  if (page === 'trading-accounts') {
    return (
      <TradingAccountsModal
        asPage
        key={`tam-page-${tradingAccounts?.initialTab || "login"}`}
        user={tradingAccounts?.user}
        initialTab={tradingAccounts?.initialTab || "login"}
        onClose={tradingAccounts?.onClose}
        onLogout={tradingAccounts?.onLogout}
        onAccountSwitch={tradingAccounts?.onAccountSwitch}
      />
    );
  }

  const dark = darkTheme;
  const MAP  = {
    'trade-report':     { icon:'📊', label:'Trade Report'                },
    ledger:             { icon:'📒', label:'Ledger Report'               },
    'order-rejection':  { icon:'❌', label:'Order/Trade Rejection Log'   },
    'order-editdelete': { icon:'✏️', label:'Order/Trade Edit/Delete Log' },
  };
  const { icon, label } = MAP[page] || { icon:'📄', label: page };
  return (
    <div style={{ padding:'24px', background: dark?'#1a0a0a':'#f4f0ec', minHeight:'100%', fontFamily: FONT_ROBOTO }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:24 }}>
        <span style={{ fontSize:16, color: dark?'#c8a87a':'#8a6030', fontWeight:700 }}>{icon} {label}</span>
      </div>
      <div style={{ background: dark?'#231212':'#ede8e3', border:`1px solid ${dark?'#3a1a1a':'#c0b8ae'}`, borderRadius:8, padding:'48px 32px', textAlign:'center', color: dark?'#664444':'#9a8878' }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🚧</div>
        <div style={{ fontSize:14, fontWeight:600, color: dark?'#c8a87a':'#8a6030', marginBottom:8 }}>{label}</div>
        <div style={{ fontSize:12 }}>This module is under construction.</div>
      </div>
    </div>
  );
}

function ShortcutsModal({ onClose, darkTheme = false }) {
  const { t } = useAppLanguage();
  return (
    <div className={`modal-overlay shortcuts-modal-overlay${darkTheme ? " shortcuts-modal-overlay--dark" : ""}`} onClick={onClose}>
      <div className={`modal-box shortcuts-modal-box${darkTheme ? " shortcuts-modal-box--dark" : ""}`} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t("keyboardShortcuts")}</h3>
          <button className="modal-close" onClick={onClose} aria-label={t("close")}>✕</button>
        </div>
        <div className="modal-body">
          <div className="shortcuts-grid">
            {SHORTCUTS.map((s,i)=>
              s.sectionKey ? (
                <div key={i} className="shortcut-section-title">{t(s.sectionKey)}</div>
              ) : (
                <div key={i} className="shortcut-row">
                  <span className="shortcut-desc">{t(s.descKey)}</span>
                  <span className="shortcut-keys" aria-label={s.key}>
                    {shortcutKeyParts(s.key).map((part, j) => (
                      <kbd key={`${i}-${j}`} className="shortcut-key">{part}</kbd>
                    ))}
                  </span>
                </div>
              )
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-confirm" onClick={onClose}>{t("gotIt")}</button>
        </div>
      </div>
    </div>
  );
}

function Toast({ msg, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2800); return ()=>clearTimeout(t); }, [onDone]);
  return <div className="toast">{msg}</div>;
}

function Mt5Icon({ name }) {
  const common = { viewBox: "0 0 24 24", "aria-hidden": true, fill: "none" };
  switch (name) {
    case "quotes":
      /* MT5 Quotes — list */
      return (
        <svg {...common}>
          <path className="fx-icon-fill" d="M3.6 6.1h2.3v2.1H3.6zm0 4.9h2.3v2.1H3.6zm0 4.8h2.3V18H3.6z" />
          <path d="M8 7.15h12.2M8 12h12.2M8 16.85h12.2" />
        </svg>
      );
    case "chart":
      /* MT5 Chart — filled candlesticks */
      return (
        <svg {...common}>
          <path className="fx-icon-fill" d="M6.1 3.4h1.5V6h2.3v8.2H7.6v2.5H6.1V14.2H3.8V6h2.3V3.4zm8.3 2.7h1.5V8.4h2.3v7.1h-2.3v2.4h-1.5V15.5h-2.3V8.4h2.3V6.1z" />
        </svg>
      );
    case "position":
      /* MT5 Trade — three lines */
      return (
        <svg {...common}>
          <path d="M5 7h14M5 12h14M5 17h14" />
        </svg>
      );
    case "tradebook":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 8v4.2l2.6 1.6" />
        </svg>
      );
    case "history":
      /* MT5 History — clock with circular arrow */
      return (
        <svg {...common}>
          <path d="M4.2 8.2A8.2 8.2 0 1 1 3.8 13" />
          <path d="M3.5 4.8v3.8h3.8" />
          <path d="M12 8.2v4.2l2.8 1.7" />
        </svg>
      );
    case "settings":
      return (
        <svg {...common}>
          <path
            className="fx-icon-fill"
            fillRule="evenodd"
            d="M10.1 2.6h3.8l.45 1.9c.55.2 1.06.5 1.52.86l1.85-.9 2.7 2.7-.9 1.85c.36.46.66.97.86 1.52l1.9.45v3.8l-1.9.45a6.4 6.4 0 0 1-.86 1.52l.9 1.85-2.7 2.7-1.85-.9a6.4 6.4 0 0 1-1.52.86l-.45 1.9h-3.8l-.45-1.9a6.4 6.4 0 0 1-1.52-.86l-1.85.9-2.7-2.7.9-1.85a6.4 6.4 0 0 1-.86-1.52l-1.9-.45v-3.8l1.9-.45c.2-.55.5-1.06.86-1.52l-.9-1.85 2.7-2.7 1.85.9c.46-.36.97-.66 1.52-.86l.45-1.9zM12 9.15a2.85 2.85 0 1 0 0 5.7 2.85 2.85 0 0 0 0-5.7z"
          />
        </svg>
      );
    default:
      return null;
  }
}

function MobileBottomNav({
  active,
  onQuotes,
  onChart,
  onTradeBook,
  onTrade,
  onSettings,
  darkTheme = false,
}) {
  const { t } = useAppLanguage();
  const items = [
    { id: "quotes", label: t("quotes"), onClick: onQuotes },
    { id: "chart", label: t("chart"), onClick: onChart },
    { id: "position", label: t("trade"), onClick: onTrade },
    { id: "tradebook", label: t("history"), onClick: onTradeBook },
    { id: "settings", label: t("settings"), onClick: onSettings },
  ];
  return (
    <nav
      className={`fx-mobile-footer${darkTheme ? " fx-mobile-footer--dark" : ""}`}
      aria-label="Mobile navigation"
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className={`fx-mobile-footer__btn${active === item.id ? " fx-mobile-footer__btn--active" : ""}`}
          onClick={item.onClick}
          aria-label={item.label}
        >
          <span className="fx-mobile-footer__icon">
            <Mt5Icon name={item.id} />
          </span>
          <span className="fx-mobile-footer__label">{item.label}</span>
        </button>
      ))}
    </nav>
  );
}

/** Group digits for display (en-IN); preserves decimals; BigInt for huge integer parts. */
function formatLedgerBalanceNumber(rawBalance) {
  if (rawBalance === undefined || rawBalance === null) return null;
  const raw = String(rawBalance).trim().replace(/,/g, "");
  if (!raw) return null;
  const neg = raw.startsWith("-");
  const unsigned = neg ? raw.slice(1) : raw;
  const [intPart, frac] = unsigned.split(".");
  if (!intPart || !/^\d+$/.test(intPart)) return raw;
  try {
    const intFmt = BigInt(intPart).toLocaleString("en-IN");
    const body = frac !== undefined && frac !== "" ? `${intFmt}.${frac}` : intFmt;
    return neg ? `-${body}` : body;
  } catch {
    const n = Number(unsigned);
    if (!Number.isFinite(n)) return raw;
    return (neg ? "-" : "") + n.toLocaleString("en-IN", { maximumFractionDigits: 12 });
  }
}

function formatLedgerBalance(availableBalance) {
  if (!availableBalance || typeof availableBalance.balance === "undefined" || availableBalance.balance === null) {
    return null;
  }
  const num = formatLedgerBalanceNumber(availableBalance.balance);
  if (num == null) return null;
  const crDr = availableBalance.crDr ? ` ${availableBalance.crDr}` : "";
  return `${num}${crDr}`;
}

function ledgerBalanceColor(availableBalance) {
  if (availableBalance?.crDr === "Cr") return "var(--green)";
  if (availableBalance?.crDr === "Dr") return "var(--red)";
  return "var(--green)";
}

export default function TradingPlatform({ user, onLogout }) {
  useBrokerTimeZoneId();
  const dispatch = useDispatch();
  const { availableBalance, availableBalanceLoading } = useSelector((s) => s.common);
  const { marginSummary, marginSummaryLoading } = useSelector((s) => s.margin);
  const { positionReportList } = useSelector((s) => s.positionReport || {});
  const liveMarginSummary = useMemo(() => {
    const livePnl = sumLivePositionPnl(positionReportList);
    // Prefer live open-position MTM whenever we have rows (even if sum is 0).
    if (positionReportList?.length) {
      return applyLivePnlToMarginSummary(marginSummary, livePnl);
    }
    return marginSummary;
  }, [marginSummary, positionReportList]);

  const tradePnlTotal = useMemo(
    () => sumLivePositionPnl(positionReportList),
    [positionReportList]
  );

  const [menuOpen,            setMenuOpen]            = useState(false);
  const [symbolPanelOpen,     setSymbolPanelOpen]     = useState(false);
  const [mwBrowseMode,        setMwBrowseMode]        = useState(false);
  const mwMobileRef = useRef(null);
  const [darkTheme,           setDarkTheme]           = useState(() => {
    try {
      const stored = localStorage.getItem("fx_ui_theme");
      if (stored === "light") return false;
      if (stored === "dark") return true;
    } catch (e) {
      /* ignore */
    }
    return true;
  });
  const [showWalletHub,       setShowWalletHub]       = useState(false);
  const [walletHubInitialTab, setWalletHubInitialTab] = useState("overview");
  const [showShortcuts,       setShowShortcuts]       = useState(false);
  const [showAboutProgram,    setShowAboutProgram]    = useState(false);
  const [showTradingAccounts, setShowTradingAccounts] = useState(false);
  const [tradingAccountsInitialTab, setTradingAccountsInitialTab] = useState("login");
  const [toast,               setToast]               = useState(null);
  /** MT5-style hover flyout beside the side menu (desktop). */
  const [menuFlyout, setMenuFlyout] = useState(null);
  const menuFlyoutCloseTimerRef = useRef(null);
  const [settingsTip, setSettingsTip] = useState(null);
  const settingsTipTimerRef = useRef(null);
  const [colorTemplateId, setColorTemplateId] = useState(
    () => loadChartPreferences().colorTemplateId
  );
  const [appLanguage, setAppLanguage] = useState(() => loadAppLanguage());

  useEffect(() => {
    applyAppLanguage(appLanguage.id);
    const onLang = () => setAppLanguage(loadAppLanguage());
    window.addEventListener(APP_LANGUAGE_EVENT, onLang);
    return () => window.removeEventListener(APP_LANGUAGE_EVENT, onLang);
  }, []);
  const { isMobile, isTablet, isCompact, width } = useViewport();
  const [connectedAccount,    setConnectedAccount]    = useState(null);
  const [activePage,          setActivePage]          = useState(() => readHashRoute().page);
  const [historyTab,          setHistoryTab]          = useState(() => readHashRoute().historyTab);
  /** Bottom dock under chart: Trade (positions+pending) or History tab in-place (MT5-style). */
  const [dockPanel, setDockPanel] = useState("trade"); // trade | history
  const [drawingsPanelOpen, setDrawingsPanelOpen] = useState(false);
  const [drawingRailOpen, setDrawingRailOpen] = useState(true);

  // Web: drawing toolbar always on (no hide/show). Mobile: user can toggle.
  useEffect(() => {
    if (!isMobile) setDrawingRailOpen(true);
  }, [isMobile]);
  const drawingRailVisible = isMobile ? drawingRailOpen : true;
  const [dockHistoryTab, setDockHistoryTab] = useState("deals");
  const [panelExpanded,       setPanelExpanded]       = useState(false);
  const [selectedChartRow, setSelectedChartRow] = useState(null);
  const [selectedChartSymbol, setSelectedChartSymbol] = useState("");
  const [quickOrderRow, setQuickOrderRow] = useState(null);
  const [symbolPanelWidth, setSymbolPanelWidth] = useState(WATCHLIST_PANEL_DEFAULT_WIDTH);
  const [watchlistDockHidden, setWatchlistDockHidden] = useState(false);
  const watchlistWidthBeforeHideRef = useRef(WATCHLIST_PANEL_DEFAULT_WIDTH);
  const [positionDockHeight, setPositionDockHeight] = useState(() => {
    if (typeof window === "undefined") return 200;
    return Math.min(260, Math.max(POSITION_DOCK_MIN_HEIGHT, Math.round(window.innerHeight * 0.26)));
  });
  const positionDockHeightBeforeCollapseRef = useRef(200);
  const dragLayoutRef = useRef({ kind: null, start: 0, startSize: 0, pointerId: null });
  const chartLayoutRafRef = useRef(null);
  const symbolWidthRafRef = useRef(null);
  const symbolWidthPendingRef = useRef(null);
  const lastSymbolRefreshAtRef = useRef(0);
  const layoutElRef = useRef(null);
  const prevMenuOpenRef = useRef(false);

  useEffect(() => {
    // Cache main layout element for fast CSS var updates during drag (avoid querySelector per mousemove).
    if (typeof document === "undefined") return;
    layoutElRef.current = document.querySelector(".main-layout");
  }, []);

  const getSymbolPanelMaxWidth = useCallback(() => {
    const layoutEl =
      layoutElRef.current ||
      (typeof document !== "undefined" ? document.querySelector(".main-layout") : null);
    // Chart column (.fx-chart-area__main) is the true shared space with the watchlist.
    const mainEl =
      typeof document !== "undefined" ? document.querySelector(".fx-chart-area__main") : null;
    const layoutWidth =
      mainEl?.clientWidth ||
      layoutEl?.clientWidth ||
      (typeof window !== "undefined" ? window.innerWidth : 1200);
    const resizeBarWidth = 6;

    // Drag limit: MarketWatch can grow left to at most 45% of the shared chart row.
    const maxByRatio = Math.floor(layoutWidth * 0.45);
    const minChartWidth = Math.max(280, Math.floor(layoutWidth * 0.55));
    const maxByChartMin = Math.floor(layoutWidth - minChartWidth - resizeBarWidth);

    // Floor 240 so tiny windows still clamp; applySymbolPanelWidth pairs with panel min.
    return Math.max(240, Math.min(maxByRatio, maxByChartMin));
  }, []);

  /** Keep docked order form in sync when user picks another symbol while the panel is open. */
  useEffect(() => {
    setQuickOrderRow((prev) => {
      if (!prev) return prev;
      if (selectedChartRow && typeof selectedChartRow === "object") return selectedChartRow;
      return prev;
    });
  }, [selectedChartRow]);

  const scriptIndexList = useSelector((s) => s.marketWatch?.scriptIndexList);
  const marketWatchList = useSelector((s) => s.marketWatch?.marketWatchList);

  const activePageRef = useRef(activePage);

  // Restore / sync page from URL hash (refresh + browser hash edits).
  useEffect(() => {
    const openTradeDockFromLegacyHash = () => {
      setActivePage(null);
      activePageRef.current = null;
      writeAppPageToHash(null);
      setSymbolPanelOpen(false);
      setDockPanel("trade");
      setPositionDockHeight((prev) => {
        if (prev > POSITION_DOCK_SNAP_COLLAPSE) return prev;
        return positionDockHeightBeforeCollapseRef.current || 220;
      });
    };
    const syncFromHash = () => {
      const { page, historyTab: tab } = readHashRoute();
      // Web: #position → under-chart Trade dock. Mobile: full Trade page.
      if (page === "position" && !isMobile) {
        openTradeDockFromLegacyHash();
        return;
      }
      setActivePage(page);
      activePageRef.current = page;
      setHistoryTab(tab);
      if (page) setSymbolPanelOpen(false);
    };
    syncFromHash();
    window.addEventListener("hashchange", syncFromHash);
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, [isMobile]);

  const themeKey = darkTheme ? 'dark' : 'light';

  const displayName = connectedAccount?.name  || connectedAccount?.login
                   || user?.UserName || user?.userName || user?.name || user?.email || 'User';
  const displayCode = connectedAccount?.login || connectedAccount?.userCode
                   || user?.UserCode || user?.userCode || user?.id || '';

  /** Reflow chart and flex panes when viewport breakpoint or width changes. */
  useEffect(() => {
    const id = requestAnimationFrame(() => dispatchLayoutRefresh());
    return () => cancelAnimationFrame(id);
  }, [isMobile, isTablet, isCompact, width]);

  /**
   * Clamp the position dock. Dragging down can shrink to a thin grab strip so
   * chart + marketwatch fill the height; dragging up restores a usable table.
   */
  const clampDockHeight = useCallback((raw) => {
    const areaH =
      layoutElRef.current?.clientHeight ||
      (typeof window !== "undefined" ? window.innerHeight : 800);
    const max = Math.max(
      POSITION_DOCK_MIN_HEIGHT,
      Math.min(Math.floor(areaH * 0.4), areaH - CHART_MIN_HEIGHT)
    );
    return Math.min(max, Math.max(POSITION_DOCK_COLLAPSED_HEIGHT, Math.round(raw)));
  }, []);

  const isPositionDockCollapsed = positionDockHeight <= POSITION_DOCK_SNAP_COLLAPSE;

  const togglePositionDockCollapsed = useCallback(() => {
    setPositionDockHeight((prev) => {
      if (prev <= POSITION_DOCK_SNAP_COLLAPSE) {
        const restore = positionDockHeightBeforeCollapseRef.current || 200;
        return clampDockHeight(Math.max(POSITION_DOCK_MIN_HEIGHT, restore));
      }
      positionDockHeightBeforeCollapseRef.current = prev;
      return POSITION_DOCK_COLLAPSED_HEIGHT;
    });
  }, [clampDockHeight]);

  /** Synthetic layout refresh when position dock height toggles. */
  useEffect(() => {
    if (activePage) return;
    const id = requestAnimationFrame(() => dispatchLayoutRefresh());
    return () => cancelAnimationFrame(id);
  }, [activePage, positionDockHeight]);

  /** Keep the position dock from eating the chart on short viewports. */
  useEffect(() => {
    const onResize = () =>
      setPositionDockHeight((prev) => {
        if (prev <= POSITION_DOCK_SNAP_COLLAPSE) return POSITION_DOCK_COLLAPSED_HEIGHT;
        return clampDockHeight(prev);
      });
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clampDockHeight]);

  // ── Only dispatch getIndexScript here — MarketWatch.js handles getMarketWatchList
  useEffect(() => {
    dispatch(getIndexScript());
    // NOTE: getMarketWatchList is intentionally NOT called here.
    // MarketWatch.js calls it on its own mount. Calling it here too
    // caused a race condition + duplicate API calls.
  }, [dispatch]);

  useEffect(() => {
    if (user?.userId) {
      dispatch(getAvailableBalance());
      dispatch(getMarginSummary());
      dispatch(getRangeList());
    }
  }, [dispatch, user?.userId]);

  // Slow poll for Balance/Credit/Used (§11.1); P&L/Equity refresh with positions elsewhere.
  useEffect(() => {
    if (!user?.userId) return undefined;
    const id = setInterval(() => {
      dispatch(getMarginSummary());
    }, 15000);
    return () => clearInterval(id);
  }, [dispatch, user?.userId]);

  const closeMenu = useCallback(() => {
    if (menuFlyoutCloseTimerRef.current) {
      clearTimeout(menuFlyoutCloseTimerRef.current);
      menuFlyoutCloseTimerRef.current = null;
    }
    if (settingsTipTimerRef.current) {
      clearTimeout(settingsTipTimerRef.current);
      settingsTipTimerRef.current = null;
    }
    setSettingsTip(null);
    setMenuFlyout(null);
    setMenuOpen(false);
    requestAnimationFrame(() => dispatchLayoutRefresh());
  }, []);

  const hideSettingsTip = useCallback(() => {
    if (settingsTipTimerRef.current) {
      clearTimeout(settingsTipTimerRef.current);
      settingsTipTimerRef.current = null;
    }
    setSettingsTip(null);
  }, []);

  const showSettingsTip = useCallback((e, text) => {
    if (isMobile || !text) return;
    const el = e.currentTarget;
    if (!(el instanceof Element)) return;
    const r = el.getBoundingClientRect();
    const next = {
      text,
      x: Math.round(r.left + 8),
      y: Math.round(r.bottom + 3),
    };
    if (settingsTipTimerRef.current) clearTimeout(settingsTipTimerRef.current);
    settingsTipTimerRef.current = setTimeout(() => {
      setSettingsTip(next);
      settingsTipTimerRef.current = null;
    }, 280);
  }, [isMobile]);

  const cancelMenuFlyoutClose = useCallback(() => {
    if (menuFlyoutCloseTimerRef.current) {
      clearTimeout(menuFlyoutCloseTimerRef.current);
      menuFlyoutCloseTimerRef.current = null;
    }
  }, []);

  const scheduleMenuFlyoutClose = useCallback(() => {
    cancelMenuFlyoutClose();
    menuFlyoutCloseTimerRef.current = setTimeout(() => {
      setMenuFlyout(null);
      menuFlyoutCloseTimerRef.current = null;
    }, 140);
  }, [cancelMenuFlyoutClose]);

  /** MT5: open child panel flush to the right of the hovered row (aligned to row top). */
  const openMenuFlyout = useCallback((id, rowEl, items) => {
    if (!rowEl || !items?.length) return;
    cancelMenuFlyoutClose();
    const rowRect = rowEl.getBoundingClientRect();
    const menuEl = rowEl.closest(".side-menu");
    const menuRect = menuEl?.getBoundingClientRect();
    const rowH = id === "color-templates" || id === "language" ? 36 : 42;
    const approxH = items.length * rowH + 8;
    const maxTop = Math.max(8, window.innerHeight - approxH - 8);
    // Prefer aligning the submenu top with the parent row (MT5); only shift if it would clip.
    const top = rowRect.top > maxTop ? maxTop : rowRect.top;
    setMenuFlyout({
      id,
      top: Math.round(top),
      left: Math.round(menuRect ? menuRect.right - 1 : rowRect.right),
      items,
    });
  }, [cancelMenuFlyoutClose]);

  useEffect(() => {
    if (!menuOpen) {
      setMenuFlyout(null);
      hideSettingsTip();
    }
  }, [menuOpen, hideSettingsTip]);

  useEffect(() => () => hideSettingsTip(), [hideSettingsTip]);

  // Close menu when clicking anywhere outside the panel (chart, MarketWatch, header, etc.).
  // Leave the ☰ button alone so the same control still toggles open/closed.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest(".side-menu")) return;
      if (t.closest(".side-menu-flyout")) return;
      if (t.closest(".top-bar .menu-btn, .icon-btn.menu-btn")) return;
      closeMenu();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    const onPrefs = (e) => {
      const id = e.detail?.colorTemplateId ?? loadChartPreferences().colorTemplateId;
      setColorTemplateId(id);
    };
    window.addEventListener(CHART_PREF_EVENT, onPrefs);
    return () => window.removeEventListener(CHART_PREF_EVENT, onPrefs);
  }, []);

  const openColorTemplatesFlyout = useCallback(
    (rowEl) => {
      openMenuFlyout(
        "color-templates",
        rowEl,
        COLOR_TEMPLATES.map((tpl) => ({
          id: tpl.id,
          label: colorTemplateLabel(tpl, (key) => tLang(key, appLanguage.id)),
          kind: "color-template",
          upColor: tpl.upColor,
          downColor: tpl.downColor,
          templateId: tpl.id,
        }))
      );
    },
    [appLanguage.id, openMenuFlyout]
  );

  const openLanguageFlyout = useCallback(
    (rowEl) => {
      openMenuFlyout(
        "language",
        rowEl,
        SELECTABLE_APP_LANGUAGES.map((lang) => ({
          id: `lang-${lang.id}`,
          label: languageDisplayName(lang),
          kind: "language",
          languageId: lang.id,
        }))
      );
    },
    [openMenuFlyout]
  );

  const navigate = useCallback((page, opts = {}) => {
    dispatch(hideMessage());
    setMenuOpen(false);
    requestAnimationFrame(() => dispatchLayoutRefresh());
    const next = normalizeAppPage(page) || page || null;
    // Web only: #position / Position Report → Trade dock under chart
    if (next === "position" && !isMobile) {
      setActivePage(null);
      activePageRef.current = null;
      writeAppPageToHash(null);
      setSymbolPanelOpen(false);
      setDockPanel("trade");
      setPositionDockHeight((prev) => {
        if (prev > POSITION_DOCK_SNAP_COLLAPSE) return prev;
        return positionDockHeightBeforeCollapseRef.current || 220;
      });
      return;
    }
    const tab = next === "history" ? normalizeHistoryTab(opts.historyTab || "deals") : "deals";
    setActivePage(next);
    activePageRef.current = next;
    if (next === "history") setHistoryTab(tab);
    writeAppPageToHash(next, tab);
  }, [dispatch, isMobile]);

  const goBackToSettings = useCallback(() => {
    dispatch(hideMessage());
    setActivePage(null);
    activePageRef.current = null;
    writeAppPageToHash(null);
    setSymbolPanelOpen(false);
    setMenuOpen(true);
    requestAnimationFrame(() => dispatchLayoutRefresh());
  }, [dispatch]);

  const goHome = useCallback(() => {
    dispatch(hideMessage());
    setActivePage(null);
    activePageRef.current = null;
    writeAppPageToHash(null);
    setSymbolPanelOpen(false);
    closeMenu();
    // Chart remounts after History/Trade pages — force layout so TV widget gets size.
    requestAnimationFrame(() => dispatchLayoutRefresh());
  }, [dispatch, closeMenu]);

  const openTradePanel = useCallback(() => {
    if (isMobile) {
      navigate("position");
      return;
    }
    goHome();
    setDockPanel("trade");
    setPositionDockHeight((prev) => {
      if (prev > POSITION_DOCK_SNAP_COLLAPSE) return prev;
      return positionDockHeightBeforeCollapseRef.current || 220;
    });
    requestAnimationFrame(() => dispatchLayoutRefresh());
  }, [goHome, isMobile, navigate]);

  const openHistoryFromRail = useCallback(
    (tab = "deals") => {
      goHome();
      setDockHistoryTab(normalizeHistoryTab(tab));
      setDockPanel("history");
      setPositionDockHeight((prev) => {
        if (prev > POSITION_DOCK_SNAP_COLLAPSE) return Math.max(prev, 260);
        return Math.max(positionDockHeightBeforeCollapseRef.current || 280, 280);
      });
      requestAnimationFrame(() => dispatchLayoutRefresh());
    },
    [goHome]
  );

  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (quickOrderRow)       { setQuickOrderRow(null);        return; }
      if (showWalletHub)       { setShowWalletHub(false);       return; }
      if (showShortcuts)       { setShowShortcuts(false);       return; }
      if (SETTINGS_SUBPAGES.has(activePage)) { goBackToSettings(); return; }
      if (isMobile && REPORT_APP_PAGES.has(activePage)) { navigate("reports"); return; }
      if (showAboutProgram)    { setShowAboutProgram(false);    return; }
      if (showTradingAccounts) { setShowTradingAccounts(false); return; }
      if (menuOpen) {
        setMenuOpen(false);
        requestAnimationFrame(() => dispatchLayoutRefresh());
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [quickOrderRow, showWalletHub, showShortcuts, showAboutProgram, showTradingAccounts, menuOpen, activePage, goBackToSettings, isMobile, navigate]);

  const handleAccountSwitch = useCallback((account) => {
    setConnectedAccount(account);
    setToast(`✅ Switched to ${account.name||account.login} (${account.server})`);
    dispatch(getAvailableBalance());
    dispatch(getMarginSummary());
  }, [dispatch]);

  const toggleTheme = () => setDarkTheme((d) => !d);

  useEffect(() => {
    try {
      localStorage.setItem("fx_ui_theme", darkTheme ? "dark" : "light");
    } catch (e) {
      /* ignore */
    }
  }, [darkTheme]);

  useEffect(() => {
    if (activePage) setQuickOrderRow(null);
  }, [activePage]);

  const ledgerLabel = formatLedgerBalance(availableBalance);
  const ledgerColor = ledgerBalanceColor(availableBalance);
  const equityLabel = formatEquityPill(liveMarginSummary) || ledgerLabel;
  const walletLoading = availableBalanceLoading || marginSummaryLoading;

  /** Same as old web: view-password login sets `isMasterRole: false` on the session. */
  const viewOnlySession = user?.isMasterRole === false;

  const mobileFooterActive = useMemo(() => {
    if (menuOpen) return "settings";
    if (SETTINGS_SUBPAGES.has(activePage) || REPORT_APP_PAGES.has(activePage)) return "settings";
    if (activePage === "position") return "position";
    if (activePage === "tradebook" || activePage === "history") return "tradebook";
    if (symbolPanelOpen && !activePage) return "quotes";
    if (!activePage) return "chart";
    return "chart";
  }, [menuOpen, symbolPanelOpen, activePage]);

  const openMobileWatchlist = useCallback(() => {
    closeMenu();
    goHome();
    setPanelExpanded(false);
    setMwBrowseMode(false);
    setSymbolPanelOpen(true);
    requestAnimationFrame(() => mwMobileRef.current?.closeAddSymbol?.());
  }, [closeMenu, goHome]);

  const onMwBrowseModeChange = useCallback((browsing) => {
    setMwBrowseMode(Boolean(browsing));
  }, []);

  const openMobileChart = useCallback(() => {
    closeMenu();
    goHome();
    setSymbolPanelOpen(false);
  }, [closeMenu, goHome]);

  const openMobileTradeBook = useCallback(() => {
    closeMenu();
    setSymbolPanelOpen(false);
    navigate("history");
  }, [closeMenu, navigate]);

  /** Mobile Trade tab → MT5 Trade page (positions + account summary). */
  const openMobileTrade = useCallback(() => {
    closeMenu();
    setSymbolPanelOpen(false);
    navigate("position");
  }, [closeMenu, navigate]);

  const openMobileSettings = useCallback(() => {
    dispatch(hideMessage());
    setMwBrowseMode(false);
    setSymbolPanelOpen(false);
    setQuickOrderRow(null);
    setActivePage(null);
    activePageRef.current = null;
    writeAppPageToHash(null);
    setMenuOpen(true);
    requestAnimationFrame(() => dispatchLayoutRefresh());
  }, [dispatch]);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => !prev);
  }, []);

  // Keep side effects out of state-updater/render path.
  useEffect(() => {
    const wasOpen = prevMenuOpenRef.current;
    if (!wasOpen && menuOpen) {
      dispatch(hideMessage());
      dispatch(getAvailableBalance());
      dispatch(getMarginSummary());
    } else if (wasOpen && !menuOpen) {
      requestAnimationFrame(() => dispatchLayoutRefresh());
    }
    prevMenuOpenRef.current = menuOpen;
  }, [menuOpen, dispatch]);

  const applySymbolPanelWidth = useCallback((nextPx) => {
    const maxAllowed = getSymbolPanelMaxWidth();
    const minVisible = Math.min(WATCHLIST_PANEL_MIN_WIDTH, maxAllowed);
    const next = Math.min(maxAllowed, Math.max(minVisible, Math.round(nextPx)));
    symbolWidthPendingRef.current = next;
    const el = layoutElRef.current;
    if (el && !el.classList.contains("panel-expanded")) {
      // Keep CSS var in sync so chart flex space shrinks/grows 1:1 while dragging.
      el.style.setProperty("--fx-symbol-panel-width", `${next}px`);
    }
    return next;
  }, [getSymbolPanelMaxWidth]);

  const onLayoutDragMove = useCallback((e) => {
    const d = dragLayoutRef.current;
    if (!d.kind) return;
    if (d.pointerId != null && e.pointerId != null && d.pointerId !== e.pointerId) return;
    if (d.kind === "symbol") {
      const dx = d.start - e.clientX;
      // CSS var updates immediately inside apply — panel reflows live while dragging.
      applySymbolPanelWidth(d.startSize + dx);
      if (symbolWidthRafRef.current == null) {
        symbolWidthRafRef.current = requestAnimationFrame(() => {
          symbolWidthRafRef.current = null;
          const pending = symbolWidthPendingRef.current;
          // Keep pending until pointerup so release width cannot race with rAF clear.
          if (typeof pending === "number") setSymbolPanelWidth(pending);
          const now = Date.now();
          // Refresh often enough that MarketWatch density / chart stay responsive mid-drag.
          if (now - lastSymbolRefreshAtRef.current > 32) {
            lastSymbolRefreshAtRef.current = now;
            dispatchLayoutRefresh();
          }
        });
      }
    } else if (d.kind === "position") {
      const dy = d.start - e.clientY;
      const next = clampDockHeight(d.startSize + dy);
      setPositionDockHeight(next);
    }
  }, [applySymbolPanelWidth, clampDockHeight]);

  const endLayoutDrag = useCallback((e) => {
    const d = dragLayoutRef.current;
    // Ignore unrelated pointer up/cancel when a different pointer started the drag.
    if (d.pointerId != null && e?.pointerId != null && d.pointerId !== e.pointerId) return;
    const endedKind = d.kind;
    const startX = d.start;
    const startSize = d.startSize;

    if (endedKind === "symbol") {
      // Final width from release pointer — stay exactly where the user lets go.
      let finalWidth = symbolWidthPendingRef.current;
      if (e && typeof e.clientX === "number" && Number.isFinite(startX) && Number.isFinite(startSize)) {
        finalWidth = applySymbolPanelWidth(startSize + (startX - e.clientX));
      } else if (typeof finalWidth === "number") {
        finalWidth = applySymbolPanelWidth(finalWidth);
      }
      if (typeof finalWidth === "number") {
        setSymbolPanelWidth(finalWidth);
      }
      symbolWidthPendingRef.current = null;
    }

    dragLayoutRef.current = { kind: null, start: 0, startSize: 0, pointerId: null };
    const el = layoutElRef.current;
    // Do NOT removeProperty(--fx-symbol-panel-width): that falls back to 420px and snaps.
    // React state + the var we just set keep the release width.
    if (el) el.classList.remove("is-symbol-resizing");
    if (typeof document !== "undefined") {
      document.body.classList.remove("fx-symbol-resizing");
    }
    if (symbolWidthRafRef.current != null) {
      cancelAnimationFrame(symbolWidthRafRef.current);
      symbolWidthRafRef.current = null;
    }
    if (chartLayoutRafRef.current != null) {
      cancelAnimationFrame(chartLayoutRafRef.current);
      chartLayoutRafRef.current = null;
    }
    window.removeEventListener("pointermove", onLayoutDragMove);
    window.removeEventListener("pointerup", endLayoutDrag);
    window.removeEventListener("pointercancel", endLayoutDrag);

    // Drag position handle down far enough → collapse to thin strip (full chart + MW).
    if (endedKind === "position") {
      setPositionDockHeight((prev) => {
        if (prev < POSITION_DOCK_SNAP_COLLAPSE) {
          positionDockHeightBeforeCollapseRef.current =
            startSize > POSITION_DOCK_SNAP_COLLAPSE ? startSize : positionDockHeightBeforeCollapseRef.current;
          return POSITION_DOCK_COLLAPSED_HEIGHT;
        }
        if (prev < POSITION_DOCK_MIN_HEIGHT) {
          return clampDockHeight(POSITION_DOCK_MIN_HEIGHT);
        }
        return prev;
      });
    }

    requestAnimationFrame(() => dispatchLayoutRefresh());
  }, [onLayoutDragMove, clampDockHeight, applySymbolPanelWidth]);

  const startSymbolResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    try { e.currentTarget?.setPointerCapture?.(e.pointerId); } catch {}
    const el = layoutElRef.current;
    if (el) el.classList.add("is-symbol-resizing");
    if (typeof document !== "undefined") {
      document.body.classList.add("fx-symbol-resizing");
    }
    // Prefer measured panel width so drag delta matches on-screen size.
    const panelEl = typeof document !== "undefined" ? document.querySelector(".main-layout .symbol-panel") : null;
    const measured = panelEl?.getBoundingClientRect?.().width;
    const startSize =
      Number.isFinite(measured) && measured > 0 ? Math.round(measured) : symbolPanelWidth;
    dragLayoutRef.current = {
      kind: "symbol",
      start: e.clientX,
      startSize,
      pointerId: e.pointerId,
    };
    symbolWidthPendingRef.current = startSize;
    applySymbolPanelWidth(startSize);
    lastSymbolRefreshAtRef.current = 0;
    window.addEventListener("pointermove", onLayoutDragMove);
    window.addEventListener("pointerup", endLayoutDrag);
    window.addEventListener("pointercancel", endLayoutDrag);
  }, [symbolPanelWidth, onLayoutDragMove, endLayoutDrag, applySymbolPanelWidth]);

  const startPositionResize = useCallback((e) => {
    e.preventDefault();
    try { e.currentTarget?.setPointerCapture?.(e.pointerId); } catch {}
    dragLayoutRef.current = { kind: "position", start: e.clientY, startSize: positionDockHeight, pointerId: e.pointerId };
    window.addEventListener("pointermove", onLayoutDragMove);
    window.addEventListener("pointerup", endLayoutDrag);
    window.addEventListener("pointercancel", endLayoutDrag);
  }, [positionDockHeight, onLayoutDragMove, endLayoutDrag]);

  useEffect(() => {
    const clampNow = () => {
      if (watchlistDockHidden) return;
      const maxAllowed = getSymbolPanelMaxWidth();
      const minVisible = Math.min(WATCHLIST_PANEL_MIN_WIDTH, maxAllowed);
      setSymbolPanelWidth((prev) => Math.min(maxAllowed, Math.max(minVisible, prev)));
    };
    clampNow();
    window.addEventListener("resize", clampNow);
    return () => window.removeEventListener("resize", clampNow);
  }, [menuOpen, panelExpanded, getSymbolPanelMaxWidth, watchlistDockHidden]);

  const hideWatchlistPanel = useCallback(() => {
    if (isMobile) return;
    watchlistWidthBeforeHideRef.current = symbolPanelWidth;
    setWatchlistDockHidden(true);
    requestAnimationFrame(() => dispatchLayoutRefresh());
  }, [isMobile, symbolPanelWidth]);

  const showWatchlistPanel = useCallback(() => {
    if (isMobile) return;
    setWatchlistDockHidden(false);
    setSymbolPanelWidth(() => {
      const saved = watchlistWidthBeforeHideRef.current;
      const maxAllowed = getSymbolPanelMaxWidth();
      const minVisible = Math.min(WATCHLIST_PANEL_MIN_WIDTH, maxAllowed);
      const w =
        saved >= WATCHLIST_PANEL_MIN_WIDTH ? saved : WATCHLIST_PANEL_DEFAULT_WIDTH;
      return Math.min(maxAllowed, Math.max(minVisible, w));
    });
    requestAnimationFrame(() => dispatchLayoutRefresh());
  }, [getSymbolPanelMaxWidth, isMobile]);

  const openQuickOrderForRow = useCallback(
    (row) => {
      if (viewOnlySession || !row || typeof row !== "object") return;
      setQuickOrderRow(row);
    },
    [viewOnlySession]
  );

  const applyChartSelection = useCallback((row, key) => {
    const sym = String(key || chartKeyFromWatchRow(row) || "").trim();
    if (!sym || /^balance$/i.test(sym)) return false;
    const nextRow =
      row && typeof row === "object"
        ? row
        : { symbol: sym, displayName: sym, script: sym };
    setSelectedChartRow(nextRow);
    setSelectedChartSymbol(sym);
    setLastChartSelection(sym, nextRow);
    return true;
  }, []);

  /** Balance sheet: last selected → else 1st Market Watch. */
  const resolveBalanceActionTarget = useCallback(() => {
    if (selectedChartRow && selectedChartSymbol && !/^balance$/i.test(selectedChartSymbol)) {
      return { row: selectedChartRow, key: selectedChartSymbol };
    }
    const first =
      collectWatchRows(marketWatchList).find(
        (r) => r && typeof r === "object" && chartKeyFromWatchRow(r)
      ) || null;
    const key = chartKeyFromWatchRow(first);
    if (!first || !key || /^balance$/i.test(key)) return null;
    return { row: first, key };
  }, [selectedChartRow, selectedChartSymbol, marketWatchList]);

  /** History bottom sheet → Chart / New Order (resolve watchlist row like Quotes). */
  useEffect(() => {
    const onChart = (ev) => {
      const useLast = Boolean(ev?.detail?.useLastSelected);
      const symbol = String(ev?.detail?.symbol || "").trim();
      const histRow = ev?.detail?.row;
      // Balance sheet: last selected → else 1st Market Watch.
      if (useLast || !symbol || /^balance$/i.test(symbol)) {
        const target = resolveBalanceActionTarget();
        if (!target) return;
        applyChartSelection(target.row, target.key);
        setSymbolPanelOpen(false);
        setDockPanel("trade");
        goHome();
        return;
      }
      const mwRow =
        resolveWatchlistRow(histRow, marketWatchList, scriptIndexList) ||
        resolveWatchlistRow(symbol, marketWatchList, scriptIndexList);
      const key = chartKeyFromWatchRow(mwRow, symbol);
      if (!key || /^balance$/i.test(key)) return;
      applyChartSelection(
        mwRow ||
          (histRow && typeof histRow === "object"
            ? histRow
            : { symbol: key, displayName: key, script: key }),
        key
      );
      setSymbolPanelOpen(false);
      setDockPanel("trade");
      goHome();
    };
    const onOrder = (ev) => {
      const useLast = Boolean(ev?.detail?.useLastSelected);
      const symbol = String(ev?.detail?.symbol || "").trim();
      const histRow = ev?.detail?.row;
      // Balance sheet: New Order — last selected → else 1st Market Watch.
      if (useLast || !symbol || /^balance$/i.test(symbol)) {
        const target = resolveBalanceActionTarget();
        if (!target) return;
        applyChartSelection(target.row, target.key);
        setSymbolPanelOpen(false);
        setDockPanel("trade");
        goHome();
        if (!viewOnlySession) openQuickOrderForRow(target.row);
        return;
      }
      const mwRow =
        resolveWatchlistRow(histRow, marketWatchList, scriptIndexList) ||
        resolveWatchlistRow(symbol, marketWatchList, scriptIndexList);
      const key = chartKeyFromWatchRow(mwRow, symbol);
      if (!key || /^balance$/i.test(key)) return;
      const orderRow =
        mwRow ||
        (histRow && typeof histRow === "object"
          ? {
              ...histRow,
              script: histRow.script || histRow.symbol || key,
              symbol: histRow.symbol || histRow.script || key,
              displayName: histRow.displayName || histRow.script || histRow.symbol || key,
              buyPrice: histRow.buyPrice ?? histRow.bid ?? histRow.currentPrice ?? histRow.ltp ?? 0,
              sellPrice: histRow.sellPrice ?? histRow.ask ?? histRow.currentPrice ?? histRow.ltp ?? 0,
              lastTradedPrice: histRow.lastTradedPrice ?? histRow.ltp ?? histRow.currentPrice ?? 0,
            }
          : { symbol: key, displayName: key, script: key });
      applyChartSelection(orderRow, key);
      setSymbolPanelOpen(false);
      setDockPanel("trade");
      goHome();
      if (!viewOnlySession) {
        openQuickOrderForRow(orderRow);
      }
    };
    window.addEventListener("fx-history-open-chart", onChart);
    window.addEventListener("fx-history-new-order", onOrder);
    return () => {
      window.removeEventListener("fx-history-open-chart", onChart);
      window.removeEventListener("fx-history-new-order", onOrder);
    };
  }, [
    goHome,
    openQuickOrderForRow,
    viewOnlySession,
    marketWatchList,
    scriptIndexList,
    applyChartSelection,
    resolveBalanceActionTarget,
  ]);

  const openQuickOrderFromShortcut = useCallback(() => {
    if (viewOnlySession) {
      setToast("View-only login cannot place orders.");
      return;
    }
    if (quickOrderRow) {
      setQuickOrderRow(null);
      return;
    }
    if (selectedChartRow && typeof selectedChartRow === "object") {
      setQuickOrderRow(selectedChartRow);
      return;
    }
    setToast("Select a symbol in the watchlist first.");
  }, [viewOnlySession, quickOrderRow, selectedChartRow]);

  useEffect(() => {
    const onShortcutKey = (e) => {
      if (shouldIgnoreGlobalHotkeys(e)) return;

      // Keep Escape close behavior centralized.
      if (e.key === "Escape") return;

      if (matchHotkey(e, "f1")) {
        e.preventDefault();
        if (!isMobile) setShowShortcuts((v) => !v);
        return;
      }
      if (matchHotkey(e, "f2") || matchHotkey(e, "ctrl+m")) {
        e.preventDefault();
        if (activePage) {
          goHome();
          return;
        }
        if (isMobile) {
          setSymbolPanelOpen((v) => !v);
          return;
        }
        if (watchlistDockHidden) {
          showWatchlistPanel();
        } else {
          hideWatchlistPanel();
        }
        return;
      }
      if (matchHotkey(e, "f9") || matchHotkey(e, "ctrl+n")) {
        e.preventDefault();
        openQuickOrderFromShortcut();
        return;
      }
      if (matchHotkey(e, "f3") || matchHotkey(e, "alt+t")) {
        e.preventDefault();
        togglePositionDockCollapsed();
      }
    };

    window.addEventListener("keydown", onShortcutKey);
    return () => window.removeEventListener("keydown", onShortcutKey);
  }, [
    activePage,
    goHome,
    isMobile,
    watchlistDockHidden,
    showWatchlistPanel,
    hideWatchlistPanel,
    openQuickOrderFromShortcut,
    togglePositionDockCollapsed,
  ]);

  const mobileChartFocus = isMobile && !activePage && !symbolPanelOpen && !menuOpen;

  const mt5NewOrderButton = (iconOnly = false) => (
    <button
      className={`top-bar-mt5-new-order${iconOnly ? " top-bar-mt5-new-order--icon-only" : ""}${quickOrderRow ? " top-bar-mt5-new-order--active" : ""}`}
      type="button"
      disabled={viewOnlySession}
      title={
        viewOnlySession
          ? "View-only login — sign in with trading password to trade"
          : quickOrderRow
            ? "Close order panel"
            : "New order (selected watchlist symbol)"
      }
      aria-label={quickOrderRow ? "Close order panel" : "New order"}
      onClick={() => {
        if (viewOnlySession) return;
        if (quickOrderRow) {
          setQuickOrderRow(null);
          return;
        }
        if (selectedChartRow && typeof selectedChartRow === "object") {
          setQuickOrderRow(selectedChartRow);
        } else {
          setToast("Select a symbol in the watchlist first.");
        }
      }}
    >
      <svg
        className="top-bar-mt5-new-order__badge"
        viewBox="0 0 24 24"
        width="24"
        height="24"
        aria-hidden
      >
        <path
          d="M11.5 2.61A9.4 9.4 0 0 0 11.5 21.39"
          fill="none"
          stroke="#E15B4F"
          strokeWidth="5"
          strokeLinecap="butt"
        />
        <path
          d="M12.5 2.61A9.4 9.4 0 0 1 12.5 21.39"
          fill="none"
          stroke="#3B82F6"
          strokeWidth="5"
          strokeLinecap="butt"
        />
        <g stroke="#8E8E8E" strokeWidth="2.15" strokeLinecap="round">
          <line x1="12" y1="12" x2="12" y2="7.55" />
          <line x1="12" y1="12" x2="14.35" y2="14.85" />
        </g>
        <circle cx="12" cy="12" r="1.05" fill="#8E8E8E" />
      </svg>
      {!iconOnly ? <span className="top-bar-mt5-new-order__text">{tLang("newOrder", appLanguage.id)}</span> : null}
    </button>
  );

  return (
    <div
      className={`trading-platform ${themeKey}${menuOpen ? " trading-platform--menu-open" : ""}${isMobile ? " trading-platform--mobile" : ""}${isTablet ? " trading-platform--tablet" : ""}${isCompact ? " trading-platform--compact" : ""}${mobileChartFocus ? " trading-platform--mobile-chart-focus" : ""}${isMobile && symbolPanelOpen && !activePage ? " trading-platform--watchlist-open" : ""}${isMobile && quickOrderRow && !activePage ? " trading-platform--order-open" : ""}${!drawingRailVisible ? " trading-platform--drawing-rail-closed" : ""}`}
      style={{
        // Side menu + backdrop start below header; extra offset when view-only banner is shown
        ["--fx-menu-top"]: viewOnlySession ? "84px" : "52px",
        ...((!isMobile || activePage) ? { ["--fx-mobile-indices-h"]: "0px" } : {}),
      }}
    >

      <ThemeBridge darkTheme={darkTheme} />

      {/* ── TOP BAR (hidden on mobile History; hamburger hidden on Trade too) ── */}
      <header
        className={`top-bar${
          isMobile && (activePage === "history" || activePage === "tradebook")
            ? " top-bar--history-hidden"
            : isMobile && activePage === "position"
              ? " top-bar--trade-no-menu"
              : ""
        }`}
      >
        <div className="top-bar__left">
          {!(
            isMobile &&
            (activePage === "history" ||
              activePage === "tradebook" ||
              activePage === "position")
          ) ? (
            <button
              className="icon-btn menu-btn"
              onClick={toggleMenu}
              title={tLang("menu", appLanguage.id)}
              aria-label={tLang("menu", appLanguage.id)}
              aria-expanded={menuOpen}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="22" height="22" aria-hidden>
                <path fill="currentColor" d="M6 8.25h16v1.5H6v-1.5zm0 5h16v1.5H6v-1.5zm0 5h16v1.5H6v-1.5z" />
              </svg>
            </button>
          ) : null}

          {activePage && !isMobile && (
            <button onClick={goHome} style={{background:'transparent',border:'1px solid var(--border)',color:'var(--green)',padding:'3px 10px',borderRadius:3,cursor:'pointer',fontSize:11,fontWeight:700,marginRight:4}}>
              {tLang("backToChart", appLanguage.id)}
            </button>
          )}
        </div>

        {/* Desktop: MT5 toolbar — chart type · indicators · TF · New Order · zoom · camera */}
        {!activePage && !isMobile && (
          <div className="top-bar__chart-head">
            <ChartHeaderControls
              marketWatchHidden={watchlistDockHidden}
              onToggleMarketWatch={() => {
                if (watchlistDockHidden) showWatchlistPanel();
                else hideWatchlistPanel();
              }}
              tradeSlot={mt5NewOrderButton(false)}
            />
          </div>
        )}

        {/* Mobile Trade: P&L centered; chart page: Order lives in chart head (MT5); desktop page title */}
        <div
          className={`top-bar__center top-bar__center--with-new-order${
            isMobile && activePage === "position" ? " top-bar__center--trade-pnl" : ""
          }`}
        >
          {isMobile && activePage === "position" ? (
            <div className="top-bar__mt5-trade top-bar__mt5-trade--pnl-only">
              <span
                className={
                  "top-bar__mt5-trade-pnl top-bar__mt5-trade-pnl--hero" +
                  (tradePnlTotal > 0
                    ? " top-bar__mt5-trade-pnl--pos"
                    : tradePnlTotal < 0
                      ? " top-bar__mt5-trade-pnl--neg"
                      : "")
                }
              >
                {Number(tradePnlTotal).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          ) : activePage && !isMobile ? (
            <span style={{ fontWeight: 700, fontSize: 13, color: "var(--green)" }}>{getPageLabel(activePage, appLanguage.id)}</span>
          ) : null}
        </div>

      </header>

      {/* Indices under MT5 toolbar — toggle via SHOW_HEADER_INDICES */}
      {SHOW_HEADER_INDICES && !activePage && !menuOpen && (
        <div className={`top-bar-indices${isMobile ? " top-bar-indices--mobile" : " top-bar-indices--desktop"}`}>
          <HeaderIndexStrip scriptIndexList={scriptIndexList} />
        </div>
      )}

      {viewOnlySession && (
        <div
          className="view-only-banner"
          style={{
            padding: "8px 12px",
            textAlign: "center",
            fontSize: 12,
            fontWeight: 600,
            background: "var(--bg-secondary)",
            color: "var(--text-muted)",
            borderBottom: "1px solid var(--border)",
          }}
        >
          View only — you signed in with a <strong>view password</strong>. Charts and reports work; use your{" "}
          <strong>trading password</strong> to place orders.
        </div>
      )}

      {menuOpen && (
        <>
          {/* Below .top-bar (see z-index in SCSS); dims chart/watchlist; does not cover header */}
          <div
            className="side-menu-backdrop"
            role="presentation"
            aria-hidden
            onClick={closeMenu}
          />
          <nav className="side-menu side-menu--mt5" aria-label="Main menu">
            {isMobile ? (
              <div className="fx-mt5-settings-hero">
                <div className="fx-mt5-settings-hero__bar">
                  <h1 className="fx-mt5-settings-hero__title">{tLang("settings", appLanguage.id)}</h1>
                </div>
                <div className="fx-mt5-settings-hero__account">
                  {connectedAccount?.isDemo ? (
                    <span className="fx-mt5-settings-hero__demo" aria-hidden>
                      Demo
                    </span>
                  ) : null}
                  <div className="fx-mt5-settings-hero__name">{displayName}</div>
                  {displayCode || connectedAccount?.server ? (
                    <div className="fx-mt5-settings-hero__meta">
                      {[displayCode, connectedAccount?.server].filter(Boolean).join(" - ")}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="side-menu__header">
                <div className="account-name">{displayName}</div>
                {displayCode ? <div className="account-id">{displayCode}</div> : null}
              </div>
            )}
            <ul className="menu-list">

              <li
                className={`menu-item menu-item--section-gap${menuFlyout?.id === "trading-accounts" ? " menu-item--flyout-open" : ""}`}
                onMouseEnter={(e) => {
                  showSettingsTip(e, tLang("tradingAccounts", appLanguage.id));
                  if (isMobile) return;
                  openMenuFlyout("trading-accounts", e.currentTarget, [
                    {
                      id: "tam-login",
                      label: tLang("loginRegister", appLanguage.id),
                      iconId: "accounts",
                      action: "tam-login",
                    },
                    {
                      id: "tam-accounts",
                      label: tLang("accounts", appLanguage.id),
                      iconId: "accounts",
                      action: "tam-accounts",
                    },
                  ]);
                }}
                onMouseLeave={() => {
                  hideSettingsTip();
                  if (!isMobile) scheduleMenuFlyoutClose();
                }}
                onClick={() => {
                  setTradingAccountsInitialTab("login");
                  if (isMobile) {
                    navigate("trading-accounts");
                  } else {
                    setShowTradingAccounts(true);
                  }
                  closeMenu();
                }}
              >
                <Mt5MenuIcon id="accounts" />
                <span className="menu-label">{tLang("tradingAccounts", appLanguage.id)}</span>
                <span className="menu-arrow" aria-hidden />
              </li>

              <li
                className="menu-item"
                onMouseEnter={(e) => showSettingsTip(e, tLang("chartSettings", appLanguage.id))}
                onMouseLeave={hideSettingsTip}
                onClick={() => {
                  // Same TradingView chart settings content; mobile opens full-bleed.
                  if (isMobile) {
                    // Keep Settings on screen until the dialog exists. Closing
                    // the menu first showed Chart when the widget was not ready.
                    setActivePage(null);
                    activePageRef.current = null;
                    writeAppPageToHash(null);
                    setSymbolPanelOpen(false);
                    openChartPropertiesMobile({
                      onOpen: () => {
                        setDrawingRailOpen(false);
                        closeMenu();
                      },
                    });
                    return;
                  }
                  closeMenu();
                  executeAction("chartProperties");
                }}
              >
                <Mt5MenuIcon id="chartSettings" />
                <span className="menu-label">{tLang("chartSettings", appLanguage.id)}</span>
                <span className="menu-arrow" aria-hidden />
              </li>

              <li
                className={`menu-item${menuFlyout?.id === "wallet" ? " menu-item--flyout-open" : ""}`}
                style={{ opacity: user?.userId ? 1 : 0.45 }}
                onMouseEnter={(e) => {
                  showSettingsTip(e, tLang("wallet", appLanguage.id));
                  if (isMobile || !user?.userId) return;
                  openMenuFlyout("wallet", e.currentTarget, [
                    { id: "wh-overview", label: tLang("overview", appLanguage.id), iconId: "walletOverview", action: "wallet-overview" },
                    { id: "wh-add", label: tLang("addFunds", appLanguage.id), iconId: "walletAddFunds", action: "wallet-add" },
                    { id: "wh-withdraw", label: tLang("withdraw", appLanguage.id), iconId: "walletWithdraw", action: "wallet-withdraw" },
                    { id: "wh-statement", label: tLang("statement", appLanguage.id), iconId: "walletStatement", action: "wallet-statement" },
                  ]);
                }}
                onMouseLeave={() => {
                  hideSettingsTip();
                  if (!isMobile) scheduleMenuFlyoutClose();
                }}
                onClick={() => {
                  if (!user?.userId) return;
                  setWalletHubInitialTab("overview");
                  setShowWalletHub(true);
                  closeMenu();
                }}
              >
                <Mt5MenuIcon id="wallet" />
                <span className="menu-label">{tLang("wallet", appLanguage.id)}</span>
                <span className="menu-arrow" aria-hidden />
              </li>

              <li
                className="menu-item menu-item--section-gap"
                onMouseEnter={(e) =>
                  showSettingsTip(e, darkTheme ? tLang("lightTheme", appLanguage.id) : tLang("darkTheme", appLanguage.id))
                }
                onMouseLeave={hideSettingsTip}
                onClick={toggleTheme}
              >
                <Mt5MenuIcon id={darkTheme ? "lightTheme" : "darkTheme"} className="menu-icon--accent" />
                <span className="menu-label">{darkTheme ? tLang("lightTheme", appLanguage.id) : tLang("darkTheme", appLanguage.id)}</span>
              </li>

              <li
                className={`menu-item${menuFlyout?.id === "color-templates" ? " menu-item--flyout-open" : ""}`}
                onMouseEnter={(e) => {
                  showSettingsTip(e, tLang("colorTemplates", appLanguage.id));
                  if (isMobile) return;
                  openColorTemplatesFlyout(e.currentTarget);
                }}
                onMouseLeave={() => {
                  hideSettingsTip();
                  if (!isMobile) scheduleMenuFlyoutClose();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  // Mobile: MT5 full-page Color Templates list. Desktop: hover flyout.
                  if (isMobile) {
                    navigate("color-templates");
                    return;
                  }
                  openColorTemplatesFlyout(e.currentTarget);
                }}
              >
                <Mt5MenuIcon id="colorTemplates" />
                <span className="menu-label">{tLang("colorTemplates", appLanguage.id)}</span>
                <span className="menu-arrow" aria-hidden />
              </li>

              {SIDEBAR_NAV.filter((item) => !(isMobile && item.page === "orderbook")).map((item, navIdx) =>
                item.children ? (
                  <React.Fragment key={item.id}>
                    <li
                      className={`menu-item${navIdx === 0 ? " menu-item--section-gap" : ""}${menuFlyout?.id === item.id ? " menu-item--flyout-open" : ""}`}
                      onMouseEnter={(e) => {
                        showSettingsTip(e, item.id === "reports" ? tLang("reports", appLanguage.id) : item.label);
                        if (isMobile) return;
                        openMenuFlyout(
                          item.id,
                          e.currentTarget,
                          item.children.map((child) => ({
                            id: child.id,
                            label: child.labelKey ? tLang(child.labelKey, appLanguage.id) : child.label,
                            page: child.page,
                            iconId: child.iconId || item.iconId,
                          }))
                        );
                      }}
                      onMouseLeave={() => {
                        hideSettingsTip();
                        if (!isMobile) scheduleMenuFlyoutClose();
                      }}
                      onClick={() => {
                        // Mobile: open Reports as its own page (like Color Templates).
                        if (isMobile) {
                          navigate("reports");
                          return;
                        }
                      }}
                    >
                      <div className="menu-item__main">
                        <Mt5MenuIcon id={item.iconId} />
                        <span className="menu-label">{item.id === "reports" ? tLang("reports", appLanguage.id) : item.label}</span>
                      </div>
                      <span className="menu-arrow" aria-hidden />
                    </li>
                  </React.Fragment>
                ) : (
                  <li
                    key={item.id}
                    className={`menu-item${navIdx === 0 ? " menu-item--section-gap" : ""}${activePage===item.page ? " menu-item--active" : ""}`}
                    onMouseEnter={(e) =>
                      showSettingsTip(
                        e,
                        item.id === "history"
                          ? tLang("history", appLanguage.id)
                          : item.id === "banned"
                            ? tLang("banned", appLanguage.id)
                            : item.id === "maxqty"
                              ? tLang("maxqty", appLanguage.id)
                              : item.label
                      )
                    }
                    onMouseLeave={hideSettingsTip}
                    onClick={() => navigate(item.page)}
                  >
                    <Mt5MenuIcon id={item.iconId} />
                    <span className="menu-label">
                      {item.id === "history"
                        ? tLang("history", appLanguage.id)
                        : item.id === "banned"
                          ? tLang("banned", appLanguage.id)
                          : item.id === "maxqty"
                            ? tLang("maxqty", appLanguage.id)
                            : item.label}
                    </span>
                    {item.id !== "maxqty" ? (
                      <span className="menu-arrow" aria-hidden />
                    ) : null}
                  </li>
                )
              )}

              {!isMobile && (
              <li
                className="menu-item menu-item--section-gap"
                onMouseEnter={(e) => showSettingsTip(e, tLang("shortcuts", appLanguage.id))}
                onMouseLeave={hideSettingsTip}
                onClick={()=>{ setShowShortcuts(true); closeMenu(); }}
              >
                <Mt5MenuIcon id="shortcuts" />
                <span className="menu-label">{tLang("shortcuts", appLanguage.id)}</span>
              </li>
              )}

              <li
                className={`menu-item${isMobile ? " menu-item--section-gap" : ""}${menuFlyout?.id === "language" ? " menu-item--flyout-open" : ""}`}
                onMouseEnter={(e) => {
                  showSettingsTip(e, tLang("language", appLanguage.id));
                  if (isMobile) return;
                  openLanguageFlyout(e.currentTarget);
                }}
                onMouseLeave={() => {
                  hideSettingsTip();
                  if (!isMobile) scheduleMenuFlyoutClose();
                }}
                onClick={(e) => {
                  if (isMobile) {
                    navigate("language");
                    return;
                  }
                  e.preventDefault();
                  openLanguageFlyout(e.currentTarget);
                }}
              >
                <Mt5MenuIcon id="language" />
                <span className="menu-label">{tLang("language", appLanguage.id)}</span>
                <span className="menu-lang-code">{appLanguage.code}</span>
                <span className="menu-arrow" aria-hidden />
              </li>

              <li
                className="menu-item"
                onMouseEnter={(e) => showSettingsTip(e, tLang("aboutProgram", appLanguage.id))}
                onMouseLeave={hideSettingsTip}
                onClick={() => {
                  if (isMobile) {
                    navigate("about-program");
                    return;
                  }
                  setShowAboutProgram(true);
                  closeMenu();
                }}
              >
                <Mt5MenuIcon id="about" />
                <span className="menu-label">{tLang("aboutProgram", appLanguage.id)}</span>
              </li>

            </ul>

            <div
              className="menu-logout"
              onMouseEnter={(e) => showSettingsTip(e, tLang("logout", appLanguage.id))}
              onMouseLeave={hideSettingsTip}
              onClick={onLogout}
            >
              <Mt5MenuIcon id="logout" />
              <span className="menu-label">{tLang("logout", appLanguage.id)}</span>
            </div>
          </nav>

          {/* MT5 hover flyout — child items of the hovered parent, docked to its right */}
          {menuFlyout && (
            <div
              className={`side-menu-flyout${
                menuFlyout.id === "color-templates" || menuFlyout.id === "language"
                  ? " side-menu-flyout--color-templates"
                  : ""
              }`}
              style={{ top: menuFlyout.top, left: menuFlyout.left }}
              onMouseEnter={cancelMenuFlyoutClose}
              onMouseLeave={scheduleMenuFlyoutClose}
              role="menu"
            >
              {menuFlyout.items.map((child) => {
                const isColorTpl = child.kind === "color-template";
                const isLang = child.kind === "language";
                const isActive = isColorTpl
                  ? colorTemplateId === child.templateId
                  : isLang
                    ? appLanguage.id === child.languageId
                    : child.page && activePage === child.page;
                return (
                  <button
                    key={child.id}
                    type="button"
                    className={`side-menu-flyout__item${
                      isActive ? " side-menu-flyout__item--active" : ""
                    }${isColorTpl ? " side-menu-flyout__item--color-tpl" : ""}`}
                    role="menuitem"
                    aria-checked={isColorTpl || isLang ? isActive : undefined}
                    onMouseEnter={(e) => showSettingsTip(e, child.label)}
                    onMouseLeave={hideSettingsTip}
                    onClick={() => {
                      if (isColorTpl) {
                        applyColorTemplateId(child.templateId, () =>
                          setToast(
                            `${tLang("colorTemplates", appLanguage.id)}: ${colorTemplateLabel(
                              getColorTemplate(child.templateId),
                              (key) => tLang(key, appLanguage.id)
                            )}`
                          )
                        );
                        setColorTemplateId(child.templateId);
                        // Keep flyout open like MT5 so the checkmark updates in place.
                        return;
                      }
                      if (isLang) {
                        saveAppLanguage(child.languageId);
                        return;
                      }
                      if (child.page) {
                        navigate(child.page);
                      } else if (child.action === "tam-login") {
                        setTradingAccountsInitialTab("login");
                        setShowTradingAccounts(true);
                        closeMenu();
                      } else if (child.action === "tam-accounts") {
                        setTradingAccountsInitialTab("accounts");
                        setShowTradingAccounts(true);
                        closeMenu();
                      } else if (child.action === "wallet-overview") {
                        setWalletHubInitialTab("overview");
                        setShowWalletHub(true);
                        closeMenu();
                      } else if (child.action === "wallet-add") {
                        setWalletHubInitialTab("add");
                        setShowWalletHub(true);
                        closeMenu();
                      } else if (child.action === "wallet-withdraw") {
                        setWalletHubInitialTab("withdraw");
                        setShowWalletHub(true);
                        closeMenu();
                      } else if (child.action === "wallet-statement") {
                        setWalletHubInitialTab("statement");
                        setShowWalletHub(true);
                        closeMenu();
                      }
                      setMenuFlyout(null);
                    }}
                  >
                    {isColorTpl ? (
                      <span className="side-menu-flyout__candle">
                        <TemplateCandleIcon
                          upColor={child.upColor}
                          downColor={child.downColor}
                          compact
                        />
                      </span>
                    ) : child.iconId ? (
                      <Mt5MenuIcon id={child.iconId} />
                    ) : null}
                    <span className="side-menu-flyout__label">{child.label}</span>
                    {isColorTpl || isLang ? (
                      isActive ? (
                        <span className="side-menu-flyout__check" aria-label="Selected">
                          <ColorTemplateCheckMark />
                        </span>
                      ) : (
                        <span className="side-menu-flyout__check side-menu-flyout__check--empty" />
                      )
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── MAIN LAYOUT ── */}
      <div
        className={`main-layout ${panelExpanded ? 'panel-expanded' : ''}`}
        style={{
          ["--fx-symbol-panel-width"]:
            !activePage && (isMobile || !watchlistDockHidden) ? `${symbolPanelWidth}px` : undefined,
        }}
      >
        {activePage ? (
          <div
            className={`fx-page-scroll${isMobile && !SETTINGS_SUBPAGES.has(activePage) ? " fx-mobile-page-scroll" : ""}${isMobile && activePage && !SETTINGS_SUBPAGES.has(activePage) && activePage !== "position" && activePage !== "history" && activePage !== "tradebook" ? " fx-mobile-book-page" : ""}${isMobile && activePage === "position" ? " fx-mobile-trade-page" : ""}${isMobile && (activePage === "history" || activePage === "tradebook") ? " fx-mobile-history-page" : ""}${SETTINGS_SUBPAGES.has(activePage) ? " fx-mobile-color-templates-page" : ""}`}
            style={
              SETTINGS_SUBPAGES.has(activePage) ||
              (isMobile && (activePage === "position" || activePage === "history" || activePage === "tradebook"))
                ? { flex: 1, minWidth: 0, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }
                : { flex: 1, overflowY: "auto", minWidth: 0 }
            }
          >
            {/* Trade / History: title in top bar — no "← Chart" page header */}
            {isMobile &&
              activePage !== "position" &&
              activePage !== "history" &&
              activePage !== "tradebook" &&
              activePage !== "trading-accounts" && (
              <div
                className={`fx-mobile-page-header${SETTINGS_SUBPAGES.has(activePage) || REPORT_APP_PAGES.has(activePage) ? " fx-mobile-page-header--mt5-settings" : ""}`}
              >
                <button
                  type="button"
                  className="fx-mobile-page-header__back"
                  onClick={
                    SETTINGS_SUBPAGES.has(activePage)
                      ? goBackToSettings
                      : REPORT_APP_PAGES.has(activePage)
                        ? () => navigate("reports")
                        : goHome
                  }
                  aria-label={
                    SETTINGS_SUBPAGES.has(activePage)
                      ? "Back to settings"
                      : REPORT_APP_PAGES.has(activePage)
                        ? "Back to reports"
                        : "Back to chart"
                  }
                >
                  {SETTINGS_SUBPAGES.has(activePage) || REPORT_APP_PAGES.has(activePage) ? (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="M14.5 6.5L9 12l5.5 5.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    tLang("backToChart", appLanguage.id)
                  )}
                </button>
                <h1 className="fx-mobile-page-header__title">{getPageLabel(activePage, appLanguage.id)}</h1>
                {SETTINGS_SUBPAGES.has(activePage) || REPORT_APP_PAGES.has(activePage) ? (
                  <span className="fx-mobile-page-header__spacer" aria-hidden />
                ) : null}
              </div>
            )}
            <PageRouter
              page={activePage}
              historyTab={historyTab}
              darkTheme={darkTheme}
              languageId={appLanguage.id}
              onLanguageSelect={(id) => setAppLanguage(loadAppLanguage())}
              onColorTemplateApplied={(label) =>
                setToast(`${tLang("colorTemplates", appLanguage.id)}: ${label}`)
              }
              onOpenReport={(reportPage) => navigate(reportPage)}
              tradingAccounts={{
                user,
                initialTab: tradingAccountsInitialTab,
                onClose: goBackToSettings,
                onLogout,
                onAccountSwitch: handleAccountSwitch,
              }}
            />
          </div>
        ) : (
          <div
            className={`fx-chart-area${drawingRailVisible ? " is-drawing-open" : " is-drawing-closed"}`}
          >
            <div className="fx-chart-tools-col">
              {isMobile && (
                <button
                  type="button"
                  className={`fx-chart-tools-toggle${drawingRailOpen ? " is-active" : ""}`}
                  title={drawingRailOpen ? "Hide drawing tools" : "Show drawing tools"}
                  aria-label={drawingRailOpen ? "Hide drawing tools" : "Show drawing tools"}
                  aria-pressed={drawingRailOpen}
                  onClick={() => setDrawingRailOpen((v) => !v)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="22" height="22" aria-hidden>
                    <path fill="currentColor" d="M6 8.25h16v1.5H6v-1.5zm0 5h16v1.5H6v-1.5zm0 5h16v1.5H6v-1.5z" />
                  </svg>
                </button>
              )}
              {drawingRailVisible && (
                <ChartToolRail
                  isMobile={isMobile}
                  dockPanel={dockPanel}
                  drawingsOpen={drawingsPanelOpen}
                  onToggleDrawings={() => setDrawingsPanelOpen((v) => !v)}
                  onOpenTradePanel={openTradePanel}
                  onOpenHistory={isMobile ? openMobileTradeBook : openHistoryFromRail}
                />
              )}
            </div>
            {!isMobile && !activePage && watchlistDockHidden && (
              <button
                type="button"
                className="fx-watchlist-edge-restore"
                title="Show watchlist panel"
                aria-label="Show watchlist panel"
                onClick={showWatchlistPanel}
              >
                <span className="fx-watchlist-edge-restore__label">MarketWatch</span>
              </button>
            )}
            <div className="fx-chart-area__stack">
            {isMobile && !activePage && (
              <div className="fx-mobile-chart-head">
                <ChartHeaderControls tradeSlot={mt5NewOrderButton(true)} />
              </div>
            )}
            <main className="fx-chart-area__main">
              <ManageDrawingsPanel
                open={drawingsPanelOpen}
                onClose={() => setDrawingsPanelOpen(false)}
              />
              <section className="fx-chart-pane">
                {!activePage && quickOrderRow && (
                  <MarketWatchQuickOrderModal
                    docked={!isMobile}
                    isMobile={isMobile}
                    darkTheme={darkTheme}
                    open
                    row={quickOrderRow}
                    menuOpen={menuOpen}
                    onClose={() => setQuickOrderRow(null)}
                  />
                )}
                <MarketWatchChart
                  symbol={selectedChartSymbol}
                  selectedRow={selectedChartRow}
                  darkMode={darkTheme}
                  menuOpen={menuOpen}
                  viewOnly={viewOnlySession}
                  isMobile={isMobile}
                  orderPanelOpen={!!quickOrderRow}
                  onOpenOrder={(row) => {
                    if (viewOnlySession) return;
                    if (quickOrderRow) {
                      setQuickOrderRow(null);
                      return;
                    }
                    const target =
                      row && typeof row === "object"
                        ? row
                        : selectedChartRow && typeof selectedChartRow === "object"
                          ? selectedChartRow
                          : null;
                    if (target) setQuickOrderRow(target);
                    else setToast("Select a symbol in the watchlist first.");
                  }}
                />
              </section>
              {isMobile ? (
                <>
                  {symbolPanelOpen && (
                    <button
                      type="button"
                      className="symbol-panel-backdrop"
                      aria-label="Close watchlist"
                      onClick={() => setSymbolPanelOpen(false)}
                    />
                  )}
                  <aside className={`symbol-panel symbol-panel--mobile-full ${symbolPanelOpen ? "open" : ""}`}>
                    <div
                      className={`fx-mobile-watchlist-header${
                        mwBrowseMode ? " fx-mobile-watchlist-header--add" : ""
                      }`}
                    >
                      {mwBrowseMode ? (
                        <>
                          <button
                            type="button"
                            className="fx-mobile-watchlist-header__back"
                            onClick={() => mwMobileRef.current?.closeAddSymbol?.()}
                            aria-label={tLang("backToQuotes", appLanguage.id)}
                          >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                              <path
                                d="M15 6l-6 6 6 6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          <h2 className="fx-mobile-watchlist-header__title">{tLang("addSymbol", appLanguage.id)}</h2>
                          <span className="fx-mobile-watchlist-header__spacer" aria-hidden />
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="fx-mobile-watchlist-header__menu"
                            onClick={openMobileSettings}
                            aria-label={tLang("menu", appLanguage.id)}
                          >
                            <svg width="22" height="22" viewBox="0 0 28 28" aria-hidden>
                              <path
                                fill="currentColor"
                                d="M6 8.25h16v1.5H6v-1.5zm0 5h16v1.5H6v-1.5zm0 5h16v1.5H6v-1.5z"
                              />
                            </svg>
                          </button>
                          <h2 className="fx-mobile-watchlist-header__title">{tLang("quotes", appLanguage.id)}</h2>
                          <button
                            type="button"
                            className="fx-mobile-watchlist-header__add"
                            onClick={() => mwMobileRef.current?.openAddSymbol?.()}
                            aria-label={tLang("addSymbol", appLanguage.id)}
                          >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                              <path
                                d="M12 5v14M5 12h14"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                    <MarketWatch
                      ref={mwMobileRef}
                      setPanelExpanded={setPanelExpanded}
                      darkTheme={darkTheme}
                      selectedChartSymbol={selectedChartSymbol}
                      viewOnly={viewOnlySession}
                      mobileFullscreen
                      watchlistMinWidthPx={400}
                      onBrowseModeChange={onMwBrowseModeChange}
                      onSymbolSelectWithRow={(symbol, row) => {
                        const key = String(
                          symbol || row?.script || row?.displayName || row?.symbol || ""
                        ).trim();
                        applyChartSelection(row ?? null, key);
                        setSymbolPanelOpen(false);
                        setMwBrowseMode(false);
                      }}
                      onOpenQuickOrder={openQuickOrderForRow}
                      onBidAskOpenOrder={(row) => openQuickOrderForRow(row)}
                    />
                  </aside>
                </>
              ) : (
                !watchlistDockHidden && (
                  <>
                    <div
                      className="fx-resize-bar fx-resize-bar--vertical"
                      role="separator"
                      aria-label="Resize watchlist width"
                      onPointerDown={startSymbolResize}
                    />
                    <aside className="symbol-panel">
                      <MarketWatch
                        setPanelExpanded={setPanelExpanded}
                        darkTheme={darkTheme}
                        selectedChartSymbol={selectedChartSymbol}
                        viewOnly={viewOnlySession}
                        watchlistMinWidthPx={WATCHLIST_PANEL_MIN_WIDTH}
                        onRequestHideWatchlistPanel={hideWatchlistPanel}
                        onSymbolSelectWithRow={(symbol, row) => {
                          const key = String(
                            symbol || row?.script || row?.displayName || row?.symbol || ""
                          ).trim();
                          applyChartSelection(row ?? null, key);
                        }}
                        onOpenQuickOrder={openQuickOrderForRow}
                        onBidAskOpenOrder={(row) => openQuickOrderForRow(row)}
                      />
                    </aside>
                  </>
                )
              )}
            </main>
            {/* Trade / positions dock — web only (mobile uses Trade tab → full page) */}
            {!isMobile && (
            <div
                className={`fx-position-sheet${isPositionDockCollapsed ? " fx-position-sheet--collapsed" : ""}`}
                style={{ height: positionDockHeight }}
              >
                <div
                  className="fx-position-sheet__resize"
                  role="separator"
                  aria-label={
                    isPositionDockCollapsed
                      ? "Drag up to show position panel"
                      : "Resize position panel height — drag down to hide"
                  }
                  title={
                    isPositionDockCollapsed
                      ? "Drag up to show positions"
                      : "Drag down to show full chart + market watch"
                  }
                  onPointerDown={startPositionResize}
                  onDoubleClick={(e) => {
                    e.preventDefault();
                    togglePositionDockCollapsed();
                  }}
                />
                {!isPositionDockCollapsed && (
                  <div
                    className="fx-position-sheet__body"
                    style={{ paddingLeft: menuOpen ? 0 : 6 }}
                  >
                    <DockTerminal
                      mode={dockPanel === "history" ? "history" : "trade"}
                      historyTab={dockHistoryTab}
                      darkTheme={darkTheme}
                      onModeChange={(m) => setDockPanel(m === "history" ? "history" : "trade")}
                      onHistoryTabChange={(tab) => {
                        setDockHistoryTab(normalizeHistoryTab(tab));
                        setDockPanel("history");
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        )}
      </div>

      {/* Desktop: hide balance strip when Trade or History dock is open (MT5). */}
      {user?.userId && !menuOpen && !activePage && !isMobile && isPositionDockCollapsed && (
        <footer className="account-margin-footer">
          <AccountMarginBar
            variant="footer"
            summary={liveMarginSummary}
            loading={marginSummaryLoading}
            onRefresh={() => dispatch(getMarginSummary())}
          />
        </footer>
      )}

      {isMobile && !quickOrderRow && (
        <MobileBottomNav
          active={mobileFooterActive}
          onQuotes={openMobileWatchlist}
          onChart={openMobileChart}
          onTradeBook={openMobileTradeBook}
          onTrade={openMobileTrade}
          onSettings={openMobileSettings}
          darkTheme={darkTheme}
        />
      )}

      {showWalletHub && user?.userId && (
        <WalletHubModal
          key={`wallet-${walletHubInitialTab}`}
          user={user}
          displayName={displayName}
          ledgerLabel={ledgerLabel}
          ledgerColor={ledgerColor}
          marginSummary={liveMarginSummary}
          marginSummaryLoading={marginSummaryLoading}
          darkTheme={darkTheme}
          initialTab={walletHubInitialTab}
          onClose={() => setShowWalletHub(false)}
          onAddSuccess={() => {
            setToast("Pay-in request submitted.");
            dispatch(getAvailableBalance());
            dispatch(getMarginSummary());
          }}
          onRefreshBalance={() => {
            dispatch(getAvailableBalance());
            dispatch(getMarginSummary());
          }}
        />
      )}
      {showShortcuts && (
        <ShortcutsModal darkTheme={darkTheme} onClose={()=>setShowShortcuts(false)}/>
      )}
      {showAboutProgram && (
        <AboutProgramModal
          darkTheme={darkTheme}
          onClose={() => setShowAboutProgram(false)}
        />
      )}
      {showTradingAccounts && !isMobile && (
        <TradingAccountsModal
          key={`tam-${tradingAccountsInitialTab}`}
          user={user}
          initialTab={tradingAccountsInitialTab}
          onClose={() => setShowTradingAccounts(false)}
          onLogout={onLogout}
          onAccountSwitch={handleAccountSwitch}
        />
      )}
      {toast && <Toast msg={toast} onDone={()=>setToast(null)}/>}
      {settingsTip &&
        createPortal(
          <div
            className="fx-mt5-tip"
            style={{ left: settingsTip.x, top: settingsTip.y }}
            role="tooltip"
          >
            {settingsTip.text}
          </div>,
          document.body
        )}
    </div>
  );
}